import { rootContainer } from '@servicetitan/react-ioc';
import { LD_SERVICE_TOKEN } from '@servicetitan/launchdarkly-service';
import {
  EXPOSED_INSTANCE_DEPENDENCIES_TOKEN,
  type ExposedInstanceDependencies,
} from '@servicetitan/web-components';
import { hostLDSdkVersion, type SharedLDService } from './sharedLDService';

/**
 * Binds the host's LaunchDarkly service into the root IoC container so every MFE
 * loaded through web-components' `Loader` shares the host's LaunchDarkly client.
 * The `Loader` reads `EXPOSED_INSTANCE_DEPENDENCIES_TOKEN` and passes the service
 * to an MFE when the SDK versions match, or when both sides support versioned
 * clients. Vite does not define the `EXPOSED_INSTANCE_DEPENDENCIES` global that
 * the startup-based hosts use, so the host provides the token itself.
 * Safe to call more than once (e.g. HMR re-running the App module). A value
 * bound earlier is kept and its other entries are not replaced.
 * @param ldService - the host LaunchDarkly service to share with MFEs
 * @param sdkVersion - version of the LaunchDarkly SDK the host bundles
 */
export function bindSharedLDService(
  ldService: SharedLDService,
  sdkVersion: string = hostLDSdkVersion,
): void {
  if (!rootContainer.isBound(LD_SERVICE_TOKEN)) {
    rootContainer.bind(LD_SERVICE_TOKEN).toConstantValue(ldService);
  }

  const launchDarkly: ExposedInstanceDependencies['launchDarkly'] = {
    version: sdkVersion,
    ldService,
  };

  if (rootContainer.isBound(EXPOSED_INSTANCE_DEPENDENCIES_TOKEN)) {
    const existing = rootContainer.get<ExposedInstanceDependencies>(
      EXPOSED_INSTANCE_DEPENDENCIES_TOKEN,
    );
    if (existing.launchDarkly) {
      return;
    }
    rootContainer.unbind(EXPOSED_INSTANCE_DEPENDENCIES_TOKEN);
    rootContainer
      .bind(EXPOSED_INSTANCE_DEPENDENCIES_TOKEN)
      .toConstantValue({ ...existing, launchDarkly });
    return;
  }

  rootContainer
    .bind(EXPOSED_INSTANCE_DEPENDENCIES_TOKEN)
    .toConstantValue({ launchDarkly });
}
